// @flow

import styled from '@emotion/styled'
import { type PageProps, graphql, navigate } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { useExperiment } from '@toggl/ab-testing'
import { colors, fonts, media } from '@toggl/style'
import {
  Accordion,
  Button,
  Card,
  ContainerSectionTitle,
  Hero,
  Responsive,
  Reviews,
  SectionBlock,
  SectionMobileNoBleedBlock,
  Table,
  text,
} from '@toggl/ui'
import { MDX } from 'components'
import { LayoutTrack } from 'components/layouts'
import { TrackDownloads } from 'components/products'
import Signup from 'components/Signup'
import SignupFormFullWidth from 'components/SignupFormFullWidth'

import { getTrackSignupUrl } from '../../../utils'

import typeof Pricing from './pricing.yml'

declare var process: WebProcess

export default function PricingPage({
  data,
  location,
}: PageProps<{ pricingYaml: Pricing }>) {
  const {
    description,
    sections: { brands, hero, billingSwitch, pricing, plans, faqData },
    title,
  } = data.pricingYaml

  const [billing, setBilling] = React.useState(billingSwitch.left.id)
  const onBillingChange = (billing) => {
    setBilling(billing)
  }

  const transformedFaqData = React.useMemo(
    () =>
      faqData.map(({ description, title }, i) => ({
        description: (
          <MDX key={i} p={text.P3}>
            {description}
          </MDX>
        ),
        title,
      })),
    [faqData]
  )

  const messagingExperimentIndex = useExperiment({
    key: 'messaging-experiment-index',
    variations: ['disabled', 'enabled'],
  })

  return (
    <LayoutTrack
      title={`${title} | Toggl Track`}
      description={description}
      ogImage={require('assets/toggl-track-pricing-plans.jpg').default}
      showRatingSchema
    >
      <SectionBlock background={colors.darkPurple100}>
        <HeroTitleBigStyled spaced={false} centered>
          <text.H1 size='medium' color={colors.peach20}>
            {hero.heading}
          </text.H1>
        </HeroTitleBigStyled>
        <PricingWrapper>
          <BillingSwitchWrapper>
            <BillingText>{billingSwitch.label}</BillingText>
            <div>
              <Button.Toggle
                promo={billingSwitch.promo}
                defaultActive={
                  billing === billingSwitch.left.id ? 'left' : 'right'
                }
                labels={[billingSwitch.left.label, billingSwitch.right.label]}
                onClick={(active) => {
                  onBillingChange(
                    active === 'left'
                      ? billingSwitch.left.id
                      : billingSwitch.right.id
                  )
                }}
              />
            </div>
          </BillingSwitchWrapper>

          <Responsive.DesktopOnly>
            <PricingCardsWrapper>
              {pricing.desktopOrder.map((plan) => {
                const { link, ...info } = pricing.cards[plan]
                return (
                  <Card.Pricing
                    {...info}
                    discount={billing === 'annual'}
                    expandButtonText={pricing.expandCardText}
                    expanded
                    key={`pricing_card_${plan}`}
                    onClick={() => {
                      if (!link) return
                      if (link === '/track/signup/') {
                        window.location.assign(getTrackSignupUrl())
                      } else {
                        navigate(link)
                      }
                    }}
                  />
                )
              })}
            </PricingCardsWrapper>
          </Responsive.DesktopOnly>
          <Responsive.MobileOnly>
            <PricingCardsWrapper>
              {pricing.mobileOrder.map((plan) => (
                <CardPricing
                  {...pricing.cards[plan]}
                  discount={billing === 'annual'}
                  expandButtonText={pricing.expandCardText}
                  key={`pricing_card_${plan}`}
                  onClick={() => {
                    const link = pricing.cards[plan].link
                    if (!link) return
                    if (link === '/track/signup/') {
                      window.location.assign(getTrackSignupUrl())
                    } else {
                      navigate(link)
                    }
                  }}
                />
              ))}
            </PricingCardsWrapper>
          </Responsive.MobileOnly>
          <CtaDisclaimer>
            <text.P2 color={colors.peach10}>{pricing.cta.disclaimer}</text.P2>
            <text.P2 color={colors.peach10}>Still unsure?</text.P2>
            <CtaLink
              as='a'
              href={pricing.cta.link}
              color={colors.peach100}
              caretDirection='right'
            >
              {pricing.cta.text}
            </CtaLink>
          </CtaDisclaimer>
        </PricingWrapper>
      </SectionBlock>

      {messagingExperimentIndex.value === 'enabled' && (
        <ExtraPointsSection background={colors.darkPurple100}>
          <ContainerSectionTitle
            title='Why teams love Toggl Track'
            titleColor={colors.peach20}
          />
          <PointsWrapper>
            <PointsSection>
              <PointsTitle color={colors.darkPurple90}>
                {' '}
                <StaticImage
                  src='./assets/icon-members-team.png'
                  alt='Icon of team members'
                  height={75}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                />
                Team time tracking with ease and accuracy
              </PointsTitle>
              <ul>
                <li>Web, mobile and desktop apps</li>
                <li>Integrations with 100+ tools</li>
                <li>Intuitive interface and data structure</li>
                <li>Alerts, reminders and admin approvals</li>
              </ul>
            </PointsSection>
            <PointsSection>
              <PointsTitle color={colors.darkPurple90}>
                <StaticImage
                  src='./assets/icon-understand-business.png'
                  alt='Icon of binoculars showing chart and money icons'
                  height={75}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                />
                Understand what’s going on in your business
              </PointsTitle>
              <ul>
                <li>
                  Track billable hours and monitor estimated vs. actual efforts
                </li>
                <li>Organize clients, projects, and tasks</li>
                <li>Stay on top of resource allocation</li>
              </ul>
            </PointsSection>
            <PointsSection>
              <PointsTitle color={colors.darkPurple90}>
                <StaticImage
                  src='./assets/icon-data-growth.png'
                  alt='Illustration of a rocket signifying growth'
                  height={75}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                />
                Make your time tracking data a source of growth
              </PointsTitle>
              <ul>
                <li>Generate accurate timesheet reports</li>
                <li>
                  Your data, your way: customizable reports with no data
                  wizardry needed
                </li>
                <li>Simplify payroll processing</li>
              </ul>
            </PointsSection>
          </PointsWrapper>
        </ExtraPointsSection>
      )}

      <Credibility
        quote={{
          text: brands.quote,
          description: brands.description,
          link: brands.link,
          image: (
            <StaticImage
              src='../../../assets/case-study-sweatandco.jpg'
              alt='Profile photo of Sweat+Co employee with a black dog'
              width={250}
              formats={['auto', 'webp', 'avif']}
              placeholder='none'
              objectFit='contain'
            />
          ),
        }}
        persuader={{
          text: brands.ctaText,
        }}
        logo={{
          image: (
            <StaticImage
              src='../../../assets/brands-oneliner.png'
              alt='Company logos: Wise, Amazon, LinkedIn, Ogilvy, SAP, anfoss, Nielsen'
              width={1000}
              formats={['auto', 'webp', 'avif']}
              placeholder='none'
              objectFit='contain'
            />
          ),
          mobileImage: (
            <StaticImage
              src='../../../assets/brands-oneliner-mobile.png'
              alt='Company logos: Wise, Amazon, LinkedIn, Ogilvy, SAP, anfoss, Nielsen'
              width={450}
              formats={['auto', 'webp', 'avif']}
              placeholder='none'
              objectFit='contain'
            />
          ),
        }}
      />

      <SectionBlock background={colors.darkPurple90}>
        <Accordion title='FAQs' sections={transformedFaqData} active={0} />

        <SignupFormFullWidth
          planId={0}
          initialError={location.state?.error}
          textColor={colors.darkPurple100}
        />
      </SectionBlock>

      <SectionBlock background={colors.darkPurple100} id='compare-plans'>
        <TablePlanComparison {...plans} />
      </SectionBlock>

      <TrackDownloads />

      <SectionMobileNoBleedBlock>
        <Responsive.DesktopOnly>
          <SignupTitle>
            Ready to get on <Rotalic>Track</Rotalic>?
          </SignupTitle>
        </Responsive.DesktopOnly>
        <Signup demoSource='pricing' />
      </SectionMobileNoBleedBlock>

      <Reviews
        image={
          <StaticImage
            src='../../../assets/reviews-badge-desktop.png'
            alt='Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd'
            width={1200}
            formats={['auto', 'webp', 'avif']}
            placeholder='none'
            objectFit='contain'
          />
        }
        mobileImage={
          <StaticImage
            src='../../../assets/reviews-badge-mobile.png'
            alt='Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd'
            width={300}
            formats={['auto', 'webp', 'avif']}
            placeholder='none'
            objectFit='contain'
          />
        }
      />
    </LayoutTrack>
  )
}

export const query = graphql`
  {
    pricingYaml {
      description
      ogImage
      sections {
        billingSwitch {
          label
          left {
            id
            label
          }
          promo
          right {
            id
            label
          }
        }
        hero {
          heading
        }
        brands {
          quote
          description
          link
          ctaText
        }
        faqData {
          title
          description {
            body
          }
        }
        plans {
          features {
            entries {
              description {
                link
                content
              }
              enterprise
              free
              id
              premium
              starter
              title
            }
            header
          }
          initialVisibleGroups
          showMoreText
          title
        }
        pricing {
          cards {
            enterprise {
              buttonText
              buttonTooltip
              features {
                text
              }
              link
              price {
                details
                value
              }
              subtitle
              title
            }
            premium {
              buttonText
              buttonTooltip
              features {
                text
                tooltip
              }
              link
              highlighted
              highlightedHeader
              price {
                details
                discountValue
                value
              }
              subtitle
              title
            }
            free {
              buttonText
              buttonTooltip
              features {
                text
                tooltip
              }
              link
              price {
                details
                value
              }
              subtitle
              title
            }
            starter {
              buttonText
              buttonTooltip
              features {
                text
                tooltip
              }
              link
              price {
                details
                discountValue
                value
              }
              subtitle
              title
            }
          }
          desktopOrder
          expandCardText
          mobileOrder
          cta {
            link
            text
            disclaimer
          }
        }
      }
      title
    }
  }
`

const TablePlanComparison = styled(Table.PlanComparison)`
  margin: 20px 0 0;
  ${media.mq[2]} {
    margin-bottom: 40px;
  }
`

const PricingWrapper = styled.div`
  margin-bottom: 40px;

  ${media.mq[1]} {
    margin-bottom: 0;
  }
`

const HeroTitleBigStyled = styled(Hero.TitleBig)`
  max-width: 850px;
  text-align: center;
  margin-top: 60px;
  ${media.mq[1]} {
    margin: 80px auto 20px;
  }
  ${media.mq[2]} {
    margin-bottom: 30px;
  }
`

const BillingSwitchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;
`

const BillingText = styled.div`
  ${text.paragraph2};
  position: relative;
  top: 25px;
  ${media.mq[1]} {
    top: 10px;
  }

  color: ${({ theme }) => theme.accent};
  text-align: center;
`

const PricingCardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  ${media.mq[2]} {
    flex-wrap: nowrap;
  }
`

const CardPricing = styled(Card.Pricing)`
  width: 100%;
  position: relative;
  flex: 1 1 45%;
  min-width: 365px;
  ${media.mq[2]} {
    flex: 1;
    min-width: auto;
  }
`

const CtaLink = styled(Button.Secondary)`
  margin: 20px 0 0;
  border: 1px solid ${colors.peach100};
  padding: 15px 20px 15px 25px;
  border-radius: 200px;
  display: inline-block;
  &:hover {
    background: ${colors.peach100};
    button {
      color: ${colors.pink100};
    }
  }
`

const CtaDisclaimer = styled.div`
  margin: 70px auto 0;
  max-width: 800px;
  text-align: center;

  p {
    margin-bottom: 15px;
  }
`.withComponent('div')

const Credibility = styled(Card.QuoteLogos)`
  background: linear-gradient(
    10deg,
    ${colors.darkPurple90} 50%,
    ${colors.darkPurple100} 50.1%
  );
  overflow-x: hidden;
`

const SignupTitle = styled(text.H2)`
  text-align: center;
  margin: 50px 0;
`

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${(p) => p.theme.primary};
`

const ExtraPointsSection = styled(SectionBlock)`
  padding-top: 0;
  ${media.mq[2]} {
    padding-bottom: 120px;
  }
`

const PointsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
  background: ${colors.peach50};
  border-radius: 10px;
  padding: 40px 35px 20px;
  ${media.mq[1]} {
    flex-wrap: nowrap;
    flex-direction: row;
  }
`

const PointsSection = styled.div`
  flex: 1;
  ul {
    padding: 0;
    list-style: none;
    margin-bottom: 0;
    li {
      padding-left: 28px;
      margin: 15px 0;
      line-height: 1.35;
      color: ${colors.darkPurple80};
      position: relative;
      &:before {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        left: 0;
        top: 2px;
        background-image: url(${require('../../../assets/icon-pink-circle-checkmark.svg')
          .default});
        background-size: contain;
        background-repeat: no-repeat;
      }
    }
  }
  ${media.mq[2]} {
    padding: 5px 15px;
  }
`
const PointsTitle = styled(text.P2)`
  font-weight: bold;
  text-align: center;
  line-height: 1.35;
  text-wrap: balance;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`
